import React from "react";
import { StickerDef } from "./StickerDesigns";

// ─── Pin (badge) stickers ─────────────────────────────────────────────────
// A "pin" is one of the user's earned badges. The badge artwork is already a
// finished PNG, so we just drop it in as-is — no circle, no frame — normalised
// to the standard sticker box so every pin inserts at the same size regardless
// of its source dimensions. The artwork is passed pre-embedded as a data: URL
// (see embedImage.ts) so the rasterizers don't taint the canvas; a failed embed
// yields dataUrl: null, which renders a simple monogram fallback.

const WHITE = "#FFFFFF";

const VB = 512;
const CX = VB / 2;
const CY = VB / 2;

export interface PinStickerInput {
    id: string;                 // badge id — used for the StickerDef key
    name: string;               // badge name — only used for the monogram fallback
    dataUrl: string | null;     // pre-embedded artwork; null → monogram fallback
}

function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "★";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
}

export function MedallionSticker({ dataUrl, label }: { dataUrl: string | null; label: string }) {
    if (dataUrl) {
        // Fit the PNG into the standard box, preserving its aspect — so a tall
        // pin and a wide pin both insert at the same overall size.
        return (
            <image
                href={dataUrl}
                xlinkHref={dataUrl}
                x={0}
                y={0}
                width={VB}
                height={VB}
                preserveAspectRatio="xMidYMid meet"
            />
        );
    }
    return (
        <text
            x={CX}
            y={CY + 46}
            fontFamily="Anton, 'Mukta Mahee', sans-serif"
            fontWeight={700}
            fontSize={150}
            textAnchor="middle"
            letterSpacing={2}
            fill={WHITE}
        >
            {initials(label)}
        </text>
    );
}

// Build one pin StickerDef per earned badge.
export function buildPinStickers(pins: PinStickerInput[]): StickerDef[] {
    return pins.map((pin) => ({
        id: `pin:${pin.id}`,
        family: "Generic",
        variant: "medallion" as never,
        render: () => <MedallionSticker dataUrl={pin.dataUrl} label={pin.name} />,
    }));
}

export const PIN_VIEWBOX = VB;
